import { Suspense, lazy, useCallback, useMemo, useState } from "react"
import type { Catalogs, Me, RequestListItem, RequestsQuery } from "@shared/api"
import { labelFor } from "@shared/catalogs"
import { PageHeader } from "../components/AppShell"
import {
  EMPTY_FILTER,
  RequestsFilterBar,
  isFilterDirty,
  type RequestsFilterValue,
} from "../components/Filters"
import {
  EmptyState,
  ErrorState,
  Pagination,
  Pill,
  SkeletonRows,
  SortHeader,
  StatusPill,
} from "../components/ui"
import { useApi } from "../hooks/useApi"
import { useDebounced, type QueryState } from "../hooks/useQueryState"
import { api } from "../services/api"
import {
  displayZoneLabel,
  formatHours,
  formatInstant,
  formatInt,
  formatProposed,
  formatRelative,
} from "../utils/datetime"
/**
 * El drawer va LAZY, y no por gusto: solo se renderiza cuando hay `?id=`, así que su
 * código, su SCSS y el editor de ocurrencias que arrastra no tienen por qué estar en el
 * chunk de la ruta inicial — que está contra su techo de 120 kB gzip (DASHBOARD-PLAN §8).
 * Al hacer editables las ocurrencias, el editor entró por acá y se pasó del presupuesto;
 * mover la frontera fue más barato que gastar el lever de @supabase/auth-js.
 */
const RequestDetailDrawer = lazy(() =>
  import("./RequestDetailDrawer").then((m) => ({ default: m.RequestDetailDrawer }))
)
import "../styles/table.scss"

const PAGE_SIZE = 50
const COLS = 10

/** El filtro se hidrata DESDE la URL, así un link con filtros es reproducible. */
function filterFromQuery(q: QueryState): RequestsFilterValue {
  return {
    ...EMPTY_FILTER,
    status: q.status ? q.status.split(",").filter(Boolean) : [],
    activity_type: q.activity_type,
    format: q.format,
    frequency: q.frequency,
    person: q.person,
    person_role: (q.person_role as RequestsFilterValue["person_role"]) ?? "any",
    from: q.from,
    to: q.to,
    date_field: (q.date_field as RequestsFilterValue["date_field"]) ?? "proposed_at",
    q: q.q ?? "",
    preset: (q.preset as RequestsFilterValue["preset"]) ?? "all",
  }
}

function filterToQuery(f: RequestsFilterValue): QueryState {
  return {
    status: f.status.length ? f.status.join(",") : undefined,
    activity_type: f.activity_type,
    format: f.format,
    frequency: f.frequency,
    person: f.person,
    person_role: f.person_role === "any" ? undefined : f.person_role,
    from: f.from,
    to: f.to,
    date_field: f.date_field === "proposed_at" ? undefined : f.date_field,
    q: f.q || undefined,
    preset: f.preset === "all" ? undefined : f.preset,
  }
}

export function RequestsPage({
  me,
  catalogs,
  query,
  setQuery,
  resetQuery,
}: {
  me: Me
  catalogs: Catalogs
  query: QueryState
  setQuery: (patch: QueryState, opts?: { replace?: boolean }) => void
  resetQuery: (next?: QueryState) => void
}) {
  const filter = useMemo(() => filterFromQuery(query), [query])
  const [limit, setLimit] = useState(PAGE_SIZE)

  const sort = (query.sort as RequestsQuery["sort"]) ?? "proposed_at"
  const dir = (query.dir as "asc" | "desc") ?? "desc"

  // Escribir no dispara una request por tecla; el resto de los filtros sí es inmediato.
  const debouncedQ = useDebounced(filter.q, 300)

  const people = useApi((signal) => api.people(signal), [])

  const listParams: RequestsQuery = useMemo(
    () => ({
      status: filter.status.length ? filter.status : undefined,
      activity_type: filter.activity_type,
      format: filter.format,
      frequency: filter.frequency,
      person: filter.person,
      person_role: filter.person ? filter.person_role : undefined,
      from: filter.from,
      to: filter.to,
      date_field: filter.date_field,
      q: debouncedQ.trim() || undefined,
      sort,
      dir,
      limit,
      offset: 0,
    }),
    [
      filter.status,
      filter.activity_type,
      filter.format,
      filter.frequency,
      filter.person,
      filter.person_role,
      filter.from,
      filter.to,
      filter.date_field,
      debouncedQ,
      sort,
      dir,
      limit,
    ]
  )

  const list = useApi((signal) => api.requests.list(listParams, signal), [listParams])

  const onFilterChange = useCallback(
    (patch: Partial<RequestsFilterValue>) => {
      setLimit(PAGE_SIZE) // cambiar un filtro vuelve a la primera página
      setQuery(filterToQuery({ ...filter, ...patch }), { replace: true })
    },
    [filter, setQuery]
  )

  const onClear = useCallback(() => {
    setLimit(PAGE_SIZE)
    resetQuery({ view: "requests" })
  }, [resetQuery])

  const onSort = useCallback(
    (field: string) => {
      const nextDir = sort === field && dir === "desc" ? "asc" : "desc"
      setQuery({ sort: field, dir: nextDir }, { replace: true })
    },
    [sort, dir, setQuery]
  )

  const zoneLabel = displayZoneLabel(me.displayTimezone)
  const rows = list.data?.rows ?? []
  const summary = list.data?.summary
  const truncated = (summary as { summary_truncated?: boolean } | undefined)?.summary_truncated

  return (
    <>
      <PageHeader
        title="Solicitudes"
        subtitle="Todo lo que se pidió con /meeting-request en Slack, con la decisión de cada una."
      />

      <RequestsFilterBar
        value={filter}
        onChange={onFilterChange}
        onClear={onClear}
        catalogs={catalogs}
        people={people.data?.rows ?? []}
        displayTimezone={me.displayTimezone}
      />

      {summary && (
        <div className="summary-line">
          <span>
            <strong>{formatInt(summary.total)}</strong> solicitudes
          </span>
          <span className="summary-sep">·</span>
          <span>
            <strong>{formatInt(summary.approved)}</strong> aprobadas
          </span>
          <span className="summary-sep">·</span>
          <span>
            <strong>{formatInt(summary.not_approved)}</strong> no aprobadas
          </span>
          <span className="summary-sep">·</span>
          <span>
            <strong>{formatInt(summary.pending)}</strong> pendientes
          </span>
          <span className="summary-sep">·</span>
          <span>
            {/* Minutos de las APROBADAS: es el único número que representa tiempo
                comprometido de verdad. */}
            <strong>
              {truncated ? "—" : `${formatInt(summary.approved_minutes)} min`}
            </strong>{" "}
            {truncated ? (
              <span title="Demasiadas filas para sumar sin paginar; acotá el rango.">
                (acotá el rango)
              </span>
            ) : (
              `(${formatHours(summary.approved_minutes)}) aprobados`
            )}
          </span>
        </div>
      )}

      {list.error ? (
        <div className="card">
          <ErrorState message={list.error.message} onRetry={list.refetch} />
        </div>
      ) : (
        <>
          <div className={list.isRefetching ? "table-wrap is-refetching" : "table-wrap"}>
            <table className="data-table">
              <thead>
                <tr>
                  <th aria-label="Alertas" />
                  <th>Estado</th>
                  <SortHeader
                    label="Propuesta"
                    field="proposed_at"
                    activeField={sort}
                    dir={dir}
                    onSort={onSort}
                  />
                  <th>Tipo</th>
                  <th>Tema</th>
                  <SortHeader
                    label="Solicitante"
                    field="requester_name"
                    activeField={sort}
                    dir={dir}
                    onSort={onSort}
                  />
                  <th>Participantes</th>
                  <SortHeader
                    label="Duración"
                    field="duration_minutes"
                    activeField={sort}
                    dir={dir}
                    onSort={onSort}
                    className="numeric"
                  />
                  <SortHeader
                    label="Solicitada"
                    field="created_at"
                    activeField={sort}
                    dir={dir}
                    onSort={onSort}
                  />
                  {/* La abreviatura de zona va UNA VEZ en el header, no en cada celda. */}
                  <th>Decisión ({zoneLabel})</th>
                </tr>
              </thead>
              <tbody>
                {list.loading && rows.length === 0 ? (
                  <SkeletonRows rows={6} cols={COLS} />
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="empty" colSpan={COLS}>
                      {isFilterDirty(filter) ? (
                        <EmptyState
                          title="No hay solicitudes con estos filtros"
                          action={
                            <button type="button" className="btn" onClick={onClear}>
                              Limpiar filtros
                            </button>
                          }
                        />
                      ) : (
                        <EmptyState
                          title="Todavía no hay solicitudes"
                          hint={
                            <>
                              Cuando alguien corra <code>/meeting-request</code> en Slack, aparecerá
                              acá.
                            </>
                          }
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <Row
                      key={r.id}
                      row={r}
                      catalogs={catalogs}
                      displayTimezone={me.displayTimezone}
                      minNoticeHours={catalogs.minNoticeHours}
                      onOpen={() => setQuery({ id: r.id })}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {list.data && rows.length > 0 && (
            <Pagination
              shown={rows.length}
              total={list.data.total}
              onMore={() => setLimit((l) => l + PAGE_SIZE)}
              busy={list.isRefetching}
            />
          )}
        </>
      )}

      {query.id && (
        // Sin fallback visible: el chunk es chico y same-origin, y un spinner que
        // parpadea sobre la lista molesta más de lo que informa. El drawer aparece
        // cuando llega, igual que esperaría alguien con la lista ya cargada.
        <Suspense fallback={null}>
          <RequestDetailDrawer
            id={query.id}
            catalogs={catalogs}
            displayTimezone={me.displayTimezone}
            onClose={() => setQuery({ id: undefined })}
          />
        </Suspense>
      )}
    </>
  )
}

function Row({
  row,
  catalogs,
  displayTimezone,
  minNoticeHours,
  onOpen,
}: {
  row: RequestListItem
  catalogs: Catalogs
  displayTimezone: string
  minNoticeHours: number
  onOpen: () => void
}) {
  const flags: { glyph: string; title: string }[] = []

  const pendingHours =
    row.status === "pending" ? (Date.now() - Date.parse(row.created_at)) / 3_600_000 : 0
  if (pendingHours > 24) {
    flags.push({ glyph: "🕒", title: `Pendiente hace más de 24 h (${Math.floor(pendingHours)} h)` })
  }
  if (!row.has_approval_message) {
    flags.push({
      glyph: "⛓",
      title: "Fila huérfana: el DM de aprobación nunca se posteó (approval_message_ts es null)",
    })
  }
  const noticeHours = (Date.parse(row.proposed_at) - Date.parse(row.created_at)) / 3_600_000
  if (noticeHours < minNoticeHours) {
    flags.push({
      glyph: "⏱",
      title: `Se pidió con menos de ${minNoticeHours} h de aviso (${Math.max(0, Math.floor(noticeHours))} h)`,
    })
  }

  const visible = row.participant_names.slice(0, 2)
  const extra = row.participant_count - visible.length

  return (
    <tr
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onOpen()
        }
      }}
      tabIndex={0}
    >
      <td className="cell-flags" title={flags.map((f) => f.title).join("\n")}>
        {flags.map((f) => f.glyph).join(" ")}
      </td>
      <td>
        <StatusPill status={row.status} label={labelFor(catalogs.requestStatuses, row.status)} />
      </td>
      <td>{formatProposed(row)}</td>
      <td>
        {labelFor(catalogs.activityTypes, row.activity_type)}
        {row.frequency && (
          <>
            {" "}
            <Pill variant="accent" title="Cadencia de la recurrencia">
              {labelFor(catalogs.frequencies, row.frequency)}
            </Pill>
          </>
        )}
      </td>
      <td>
        <div className="cell-topic" title={row.topic}>
          {row.topic}
        </div>
      </td>
      <td>
        {row.requester_name}
        {row.requester_email && <span className="cell-sub">{row.requester_email}</span>}
      </td>
      <td title={row.participant_names.join(", ")}>
        {visible.join(", ") || "—"}
        {extra > 0 && ` +${extra}`}
      </td>
      <td className="numeric">{row.duration_minutes} min</td>
      <td title={formatInstant(row.created_at, displayTimezone)}>
        {formatRelative(row.created_at)}
      </td>
      <td>
        {row.decided_by_name ? (
          <>
            {row.decided_by_name}
            <span className="cell-sub">{formatInstant(row.decided_at, displayTimezone)}</span>
          </>
        ) : (
          "—"
        )}
      </td>
    </tr>
  )
}
